import { createSlice, PayloadAction, nanoid } from '@reduxjs/toolkit';

export type ToastTone = 'success' | 'error' | 'info' | 'warning';

export interface IToast {
  id: string;
  tone: ToastTone;
  title: string;
  body?: string;
  /** Milliseconds before auto-dismiss. 0 keeps it until dismissed. */
  timeout: number;
}

export interface IToastState {
  items: IToast[];
}

const initialState: IToastState = { items: [] };

export const toastsSlice = createSlice({
  name: 'toasts',
  initialState,
  reducers: {
    pushToast: {
      reducer: (state, action: PayloadAction<IToast>) => {
        state.items.push(action.payload);
      },
      prepare: (toast: Omit<IToast, 'id' | 'timeout'> & { id?: string; timeout?: number }) => ({
        payload: {
          id: toast.id ?? nanoid(),
          tone: toast.tone,
          title: toast.title,
          body: toast.body,
          // Errors stay until dismissed; they usually need reading.
          timeout: toast.timeout ?? (toast.tone === 'error' ? 0 : 5000),
        } as IToast,
      }),
    },
    dismissToast: (state, action: PayloadAction<string>) => {
      state.items = state.items.filter(t => t.id !== action.payload);
    },
    clearToasts: state => {
      state.items = [];
    },
  },
});

export const { pushToast, dismissToast, clearToasts } = toastsSlice.actions;

export default toastsSlice.reducer;
